/**
 * Utilitários para trabalhar com geração de PDF
 */

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

/**
 * Cria uma instância de jsPDF com suporte a autoTable
 * @param {string} orientation - Orientação do PDF ('p' ou 'l')
 * @param {string} unit - Unidade de medida ('mm', 'pt', etc)
 * @param {string} format - Formato do papel ('a4', 'letter', etc)
 * @returns {Object} - Instância de jsPDF com autoTable
 */
export const createPdf = (orientation = 'l', unit = 'mm', format = 'a4') => {
  // Criar instância do jsPDF
  const pdf = new jsPDF(orientation, unit, format);
  
  // Verificar se o autoTable está disponível
  if (typeof pdf.autoTable !== 'function') {
    console.warn('autoTable não disponível, usando polyfill');
    
    // Tentar adicionar autoTable manualmente
    try {
      // Implementação básica para tabelas simples
      pdf.autoTable = function(options) {
        const { 
          startY = 20, 
          head = [], 
          body = [], 
          margin = { left: 10, right: 10 },
          theme = 'grid'
        } = options;
        
        const headStyles = options.headStyles || { 
          fillColor: [40, 50, 200], 
          textColor: [255, 255, 255],
          fontSize: 10
        };
        
        const bodyStyles = options.bodyStyles || {
          fontSize: 9,
          textColor: [20, 20, 20]
        };
        
        const colCount = head[0] ? head[0].length : (body[0] ? body[0].length : 0);
        if (!colCount) return;
        
        const pageWidth = pdf.internal.pageSize.getWidth();
        const usableWidth = pageWidth - margin.left - margin.right;
        const colWidth = usableWidth / colCount;
        
        let y = startY;
        
        // Desenhar cabeçalho
        if (head && head.length > 0) {
          pdf.setFillColor(headStyles.fillColor[0], headStyles.fillColor[1], headStyles.fillColor[2]);
          pdf.rect(margin.left, y, usableWidth, 10, 'F');
          
          pdf.setTextColor(headStyles.textColor[0], headStyles.textColor[1], headStyles.textColor[2]);
          pdf.setFontSize(headStyles.fontSize);
          
          head[0].forEach((cell, i) => {
            pdf.text(cell.toString(), margin.left + (i * colWidth) + (colWidth / 2), y + 6, { 
              align: 'center' 
            });
          });
          
          y += 10;
        }
        
        // Desenhar corpo
        if (body && body.length > 0) {
          pdf.setFontSize(bodyStyles.fontSize);
          pdf.setTextColor(bodyStyles.textColor[0], bodyStyles.textColor[1], bodyStyles.textColor[2]);
          
          body.forEach((row, rowIndex) => {
            // Desenhar fundo alternado se for grid
            if (theme === 'grid' && rowIndex % 2 === 0) {
              pdf.setFillColor(240, 240, 240);
              pdf.rect(margin.left, y, usableWidth, 8, 'F');
            }
            
            // Desenhar texto
            row.forEach((cell, colIndex) => {
              pdf.text(
                cell.toString(), 
                margin.left + (colIndex * colWidth) + (colWidth / 2), 
                y + 5, 
                { align: 'center' }
              );
            });
            
            // Linha horizontal para grid
            if (theme === 'grid') {
              pdf.setDrawColor(200, 200, 200);
              pdf.line(margin.left, y + 8, margin.left + usableWidth, y + 8);
            }
            
            y += 8;
          });
        }
        
        // Adicionar propriedade finalY para compatibilidade
        pdf.lastAutoTable = { finalY: y + 5 };
        
        return pdf;
      };
    } catch (e) {
      console.error('Erro ao criar polyfill para autoTable:', e);
    }
  }
  
  return pdf;
};

/**
 * Verifica se as bibliotecas de PDF estão carregadas corretamente
 * @returns {boolean} - true se estiverem carregadas, false caso contrário
 */
export const checkPdfLibraries = () => {
  try {
    // Verificar se jsPDF está disponível
    if (typeof jsPDF !== 'function') {
      console.error('jsPDF não está disponível');
      return false;
    }
    
    // Criar instância e verificar se autoTable foi adicionado
    const pdf = new jsPDF();
    const hasAutoTable = typeof pdf.autoTable === 'function';
    
    if (!hasAutoTable) {
      // console.warn('jsPDF está disponível, mas autoTable não foi encontrado');
    }
    
    return true;
  } catch (error) {
    console.error('Erro ao verificar bibliotecas PDF:', error);
    return false;
  }
};

// Exportar o jsPDF para uso global
export { jsPDF };

// Verificar as bibliotecas na inicialização
checkPdfLibraries(); 